import { snakeToTitleCase } from '@kwenta/sdk/utils'
import { type FC, memo } from 'react'
import styled, { css } from 'styled-components'

import Select from 'components/Select'

import { DropdownIndicator, IndicatorSeparator } from '../../../../components/Select'
import { FlexDiv, FlexDivCol } from '../../../../components/layout/flex'

import type { TableFilterType } from './TableFilters'

const TransactionsHistoryOptionsValues = ['all_transactions', 'deposit', 'withdraw'] as const
const TransactionsHistoryOptions = TransactionsHistoryOptionsValues.map((value) => ({
	value,
	label: snakeToTitleCase(value),
}))

const OrdersHistoryOptionsValues = [
	'all_orders',
	'limit',
	'stop',
	'market',
	'liquidation',
	'take_profit',
	'stop_loss',
] as const
const OrdersHistoryOptions = OrdersHistoryOptionsValues.map((value) => ({
	value,
	label: snakeToTitleCase(value),
}))

export type TransactionsHistoryType = (typeof TransactionsHistoryOptions)[number]['value']
export type TransactionsHistoryOptionsType = (typeof TransactionsHistoryOptions)[number]
export type OrdersHistoryType = (typeof OrdersHistoryOptions)[number]['value']
type OrdersHistoryOptionsType = (typeof OrdersHistoryOptions)[number]

type TxOnChange = (type: TransactionsHistoryOptionsType) => void
type OrderOnChange = (type: OrdersHistoryOptionsType) => void

type TypeFilterDropDownProps = { value?: TableFilterType; mobile?: boolean } & {
	type: 'transactions' | 'orders'
	onChange: TxOnChange | OrderOnChange
}

const TypeFilterDropDown: FC<TypeFilterDropDownProps> = memo(
	({ onChange, type, value, mobile = false }) => {
		const formatOptionLabel = ({ label }: any) => {
			return <StyledLabelContainer>{label}</StyledLabelContainer>
		}

		const getSelectValue = (value?: TableFilterType) => {
			if (!value) {
				return type === 'transactions' ? TransactionsHistoryOptions[0] : OrdersHistoryOptions[0]
			}
			if (type === 'transactions') {
				return TransactionsHistoryOptions.find((option) => option.value === value)
			} else {
				return OrdersHistoryOptions.find((option) => option.value === value)
			}
		}

		return (
			<DropDownContainer mobile={mobile}>
				<StyledNetworkSelect
					mobile={mobile}
					variant="flat"
					menuWidth={'100%'}
					onChange={onChange}
					value={getSelectValue(value)}
					maxMenuHeight={130}
					formatOptionLabel={formatOptionLabel}
					components={{ IndicatorSeparator, DropdownIndicator }}
					options={type === 'transactions' ? TransactionsHistoryOptions : OrdersHistoryOptions}
					defaultValue={TransactionsHistoryOptions[0]}
					isSearchable={false}
					hasDefaultValue={!value || value === TransactionsHistoryOptions[0].value}
				/>
			</DropDownContainer>
		)
	}
)

const StyledNetworkSelect = styled(Select)<{ hasDefaultValue: boolean }>`
	.react-select__control {
		${({ mobile }) =>
			mobile
				? css`
						border-radius: 8px;
						background-color: ${(props) => props.theme.colors.selectedTheme.input.background};
				  `
				: css`
						border: none;
						background-color: ${(props) =>
							props.theme.colors.selectedTheme.newTheme.containers.primary.background};
						&:hover {
							background-color: ${(props) =>
								props.theme.colors.selectedTheme.newTheme.containers.primary.background};
						}
				  `}
		.react-select__single-value > div {
			color: ${(props) =>
				props.hasDefaultValue
					? props.theme.colors.selectedTheme.button.secondary.text
					: props.theme.colors.selectedTheme.text.value};
		}
	}

	.react-select__menu {
		${({ mobile }) =>
			!mobile &&
			css`
				margin-top: 0px;
			`}
		background-color: ${(props) =>
			props.mobile
				? props.theme.colors.selectedTheme.input.background
				: props.theme.colors.selectedTheme.newTheme.containers.primary.background};
	}
`

const StyledLabelContainer = styled(FlexDiv)`
	justify-content: flex-start;
	gap: 5px;
	color: ${(props) => props.theme.colors.selectedTheme.text.value};
`

const DropDownContainer = styled(FlexDivCol)<{ mobile?: boolean }>`
	width: ${({ mobile }) => (mobile ? '100%' : '200px')};
	padding: ${({ mobile }) => (mobile ? '0' : '0 10px')};
`

export default TypeFilterDropDown
